/**
 * Purchase page – select a token package and "buy" tokens.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  CreditCard,
  ShoppingCart,
  ArrowLeft,
  Loader2,
} from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard } from '@fortawesome/free-solid-svg-icons';
import { faPaypal } from '@fortawesome/free-brands-svg-icons';
import { getTokenPackages, purchaseTokens } from '../services';
import type { TokenPackage, PurchaseResult } from '../types';

// Only these package IDs are available for purchase in the current beta
const ENABLED_PACKAGES = new Set(['pkg_free']);

export default function PurchasePage() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState<TokenPackage[]>([]);
  const [selected, setSelected] = useState<string>('pkg_free');
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [result, setResult] = useState<PurchaseResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      const res = await getTokenPackages();
      if (res.data) setPackages(res.data);
      setLoading(false);
    }
    load();
  }, []);

  const selectedPkg = packages.find((p) => p.id === selected);

  const handlePurchase = async () => {
    if (!selected) return;
    setPurchasing(true);
    setError(null);
    try {
      const res = await purchaseTokens({ packageId: selected, paymentMethod: 'card' });
      if (res.success && res.data) {
        setResult(res.data);
      } else {
        setError(res.error ?? 'Purchase failed. Please try again.');
      }
    } catch {
      setError('An unexpected error occurred.');
    } finally {
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-screen">
          <div className="spinner" />
          <p>Loading packages…</p>
        </div>
      </div>
    );
  }

  // Success state
  if (result) {
    return (
      <div className="page-container">
        <div className="container container-sm">
          <div className="purchase-success">
            <CheckCircle2 size={64} className="success-icon" />
            <h2>Purchase Complete!</h2>
            <p>
              <strong>{result.tokensAdded.toLocaleString()}</strong> tokens have been
              added to your account.
            </p>
            <div className="success-balance">
              New Balance: <strong>{result.newBalance.toLocaleString()}</strong> tokens
            </div>
            <div className="success-actions">
              <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </button>
              <button
                className="btn btn-outline"
                onClick={() => { setResult(null); setSelected('pkg_free'); }}
              >
                Add More
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="container">
        <div className="page-header">
          <div>
            <h1>Get Tokens</h1>
            <p>Choose a package and top up your balance</p>
          </div>
          <button className="btn btn-ghost" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={18} />
            Back to Dashboard
          </button>
        </div>

        {error && <div className="alert alert-error" style={{ marginBottom: 24 }}>{error}</div>}

        <div className="purchase-layout">
          {/* ── Left: Package Grid ─────────────────────── */}
          <div className="purchase-left">
            <h2 className="purchase-section-title">Select Package</h2>
            <div className="purchase-grid">
              {packages.map((pkg) => {
                const enabled = ENABLED_PACKAGES.has(pkg.id);
                return (
                  <button
                    key={pkg.id}
                    className={`purchase-card ${selected === pkg.id ? 'selected' : ''} ${!enabled ? 'disabled' : ''}`}
                    onClick={() => enabled && setSelected(pkg.id)}
                    disabled={!enabled}
                    title={!enabled ? 'Coming soon' : undefined}
                  >
                    {!enabled && <div className="purchase-card-soon">Coming soon</div>}
                    <h3>{pkg.name}</h3>
                    <div className="purchase-card-tokens">
                      {pkg.tokens.toLocaleString()} tokens
                    </div>
                    <div className="purchase-card-price">${pkg.priceUsd.toFixed(2)}</div>
                    <div className="purchase-card-per">
                      ${(pkg.costPerToken * 1000).toFixed(5)} / 1K tokens
                    </div>
                    {pkg.savings > 0 && (
                      <div className="purchase-card-savings">Save {pkg.savings}%</div>
                    )}
                    {selected === pkg.id && (
                      <CheckCircle2 size={20} className="purchase-check" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Right: Payment + Summary ───────────────── */}
          <div className="purchase-right">
            <h2 className="purchase-section-title">Payment</h2>

            {/* Payment method buttons – disabled until Stripe */}
            <div className="payment-methods" style={{ pointerEvents: 'none', opacity: 0.5 }}>
              {(
                [
                  { id: 'card', label: 'Credit Card', faIcon: faCreditCard },
                  { id: 'paypal', label: 'PayPal', faIcon: faPaypal },
                ] as const
              ).map((pm) => (
                <button
                  key={pm.id}
                  className={`payment-method-btn ${pm.id === 'card' ? 'active' : ''}`}
                >
                  <FontAwesomeIcon icon={pm.faIcon} className="pm-icon" />
                  <span>{pm.label}</span>
                </button>
              ))}
            </div>

            {/* Card form – disabled placeholder */}
            <div className="card-form-mock">
              <div className="form-group">
                <label>Card Number</label>
                <input type="text" placeholder="0000 0000 0000 0000" disabled />
              </div>
              <div className="card-form-row">
                <div className="form-group">
                  <label>Expiry</label>
                  <input type="text" placeholder="01/01" disabled />
                </div>
                <div className="form-group">
                  <label>CVC</label>
                  <input type="text" placeholder="000" disabled />
                </div>
              </div>
              <p className="mock-notice">
                <CreditCard size={14} /> Stripe integration coming soon.
              </p>
            </div>

            {/* Order Summary */}
            {selectedPkg && (
              <div className="order-summary">
                <h3>Order Summary</h3>
                <div className="order-line">
                  <span>{selectedPkg.name} Package</span>
                  <span>{selectedPkg.tokens.toLocaleString()} tokens</span>
                </div>
                <div className="order-line total">
                  <span>Total</span>
                  <span>${selectedPkg.priceUsd.toFixed(2)} USD</span>
                </div>
                <button
                  className="btn btn-primary btn-lg btn-block"
                  onClick={handlePurchase}
                  disabled={purchasing}
                >
                  {purchasing ? (
                    <><Loader2 size={18} className="spin" /> Processing…</>
                  ) : (
                    <><ShoppingCart size={18} /> {selectedPkg.priceUsd === 0 ? `Claim ${selectedPkg.name} Tokens` : `Select ${selectedPkg.name} – $${selectedPkg.priceUsd.toFixed(2)}`}</>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
